import { useState, useMemo, useEffect } from "react";
import { Box, LinearProgress, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import { ReferenceLine } from "recharts";
import { useDispatch } from "react-redux";
import ChartConfigModal from "@plannel/components/modal/ChartConfigModal";
import Card, { CARD_HOVER_SX, CARD_HOVER_TRANSITION } from "@plannel/components/dashboard/DashboardCard";
import { KpiCard } from "@plannel/components/dashboard/KpiCard";
import { SegToggle } from "@plannel/components/dashboard/DashboardToggle";
import { DashboardPage, DashboardTopBar, DashboardRow } from "@plannel/components/dashboard/DashboardLayout";
import DashboardCardHeader from "@plannel/components/dashboard/DashboardCardHeader";
import CompactSelect from "@plannel/components/dashboard/CompactSelect";
import SeriesLegend from "@plannel/components/dashboard/SeriesLegend";
import { DashboardComposedChart, barOrLine, areaGradientDefs, DW_CHART_TYPES } from "@plannel/components/dashboard/DashboardChart";
import { makeFmtMoney, fmtQty } from "@plannel/components/dashboard/dashboardFormat";
import useDashboardData from "@plannel/hooks/useDashboardData";
import useDashboardP13n from "@plannel/hooks/useDashboardP13n";
import ColorUtil from "@plannel/pages/utils/color-util";
import salesPlanService from "@plannel/services/dashboard/sales-plan-service";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import ZDate from "@plannel/utils/zdate";
import { FS } from "@plannel/utils/font-util";

/*
 * Sales Plan Dashboard — "이번 사이클의 판매계획이 제대로 수립·합의되고 있는가".
 * 계획이 얼마나 맞았는지(정확도·편향)는 SALES_PLAN_ACCURACY_DASHBOARD 소관 → 제목 드릴 링크만 건다.
 * 명세: saas-application/docs/sql/dw/dashboard-spec/06_sales_plan.md
 */

// 시리즈별 팔레트 인덱스 — 차트 색과 설정 모달 스와치가 공유한다(색 불일치 방지).
const COLOR_IDX = { plan: 0, actual: 2, achieve: 1, bf: 14, sf: 3, mf: 0 };

const DEFAULT_CHART_CONFIG = {
  plan: { chartType: "BAR", activeFlg: true },
  actual: { chartType: "AREA", activeFlg: true },
  achieve: { chartType: "LINE", activeFlg: true },   // % 축이라 기본 LINE
  bf: { chartType: "LINE", activeFlg: true },
  sf: { chartType: "LINE", activeFlg: true },
  mf: { chartType: "BAR", activeFlg: true },
};

// ─────────────────────────────────────────
// 계획 진행 상황 — 포지션 계층 깊이별 결재 진척
//   ★ 결재 기능이 꺼진 테넌트(progress.enabled=false)에서는 이 카드를 렌더하지 않는다.
//     0/0 을 그리면 "아무도 결재를 안 했다" 로 오독된다.
// ─────────────────────────────────────────
const PlanProgressCard = ({ progress, onOpenPostn }) => {
  const { t } = useTranslation();
  const steps = progress?.steps ?? [];
  const pending = progress?.pending ?? [];
  return (
    <Card sx={{ flex: "0 0 260px", minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <DashboardCardHeader title={t("spPlanProgress")} />
      {/* 단계 목록은 고정(flexShrink:0), 남는 세로는 아래 미제출 목록이 가져간다 */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.2, mt: 0.5, flexShrink: 0 }}>
        {steps.map((s) => {
          const pct = s.total > 0 ? Math.round((s.done / s.total) * 1000) / 10 : 0;
          return (
            <Box key={s.depth}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", mb: 0.3 }}>
                <Typography sx={{ fontSize: FS.BODY, color: "text.primary", fontWeight: 500 }}>
                  {/* 백엔드가 포지션 이름들의 공통 접두사로 단계명을 만든다. 공통부가 없으면 null → 깊이로 폴백 */}
                  {s.label ?? t("spStepLevel", { n: s.depth })}
                  {/* 최종 결재 단계 — 이 단계의 done 이 곧 사이클 확정 여부 */}
                  {s.finalStep && (
                    <Typography component="span" sx={{ fontSize: FS.CAPTION, color: "primary.main", ml: 0.5 }}>
                      {t("spFinalStep")}
                    </Typography>
                  )}
                </Typography>
                <Typography sx={{ fontSize: FS.SUB, color: "text.primary", fontWeight: 600 }}>
                  {s.done} / {s.total}
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
              <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", mt: 0.2 }}>
                {t("spInProgressCount", { count: s.inProgress })}
              </Typography>
            </Box>
          );
        })}
        {/* 결재는 켜져 있는데 포지션이 없다 = 설정 미비다. "비어 있음" 으로 끝내면 원인도 다음 행동도 없다.
            (기능 자체가 꺼진 테넌트는 부모가 카드를 아예 렌더하지 않는다 — 여기 오지 않는다) */}
        {steps.length === 0 && (
          <Box>
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("spNoPositions")}</Typography>
            {/* 포지션 마스터 권한이 없으면 안내만 남기고 링크는 그리지 않는다 */}
            {onOpenPostn && (
              <Typography
                onClick={onOpenPostn}
                sx={{
                  fontSize: FS.CAPTION, color: "primary.main", cursor: "pointer", mt: 0.3,
                  "&:hover": { textDecoration: "underline" },
                }}>
                {t("spOpenPostn")}
              </Typography>
            )}
          </Box>
        )}
      </Box>

      {/* "3/10" 은 상태만 알려준다 — 실무자가 필요한 건 누가 남았나다 */}
      {steps.length > 0 && (
        /* 목록이 길면 카드 밖으로 삐져나가 잘린다 — 카드 안에서 스크롤시킨다(minHeight:0 필수) */
        <Box sx={{ mt: 1.2, pt: 1, borderTop: 1, borderColor: "divider", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", mb: 0.4, flexShrink: 0 }}>
            {t("spPendingTitle")} ({pending.length})
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {pending.length === 0 && (
            <Typography sx={{ fontSize: FS.CAPTION, color: "success.main" }}>{t("spAllSubmitted")}</Typography>
          )}
          {pending.map((r) => (
            <Box key={`${r.depth}-${r.label}`} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
              <Typography sx={{ fontSize: FS.CAPTION, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {r.label}
              </Typography>
              <Typography sx={{ fontSize: FS.CAPTION, flexShrink: 0, color: r.status === "IN_PROGRESS" ? "warning.main" : "text.secondary" }}>
                {t(r.status === "IN_PROGRESS" ? "spStatusInProgress" : "spStatusNotStarted")}
              </Typography>
            </Box>
          ))}
          </Box>
        </Box>
      )}
    </Card>
  );
};

// ─────────────────────────────────────────
// 계획 상태 — 판매계획 vs 판매실적 (+ 달성률 우측 축)
// ─────────────────────────────────────────
const PlanStatusChart = ({ data, currencyCd, unit, currentBucket, chartConfig, onOpenConfig, onToggleSeries, onDrill }) => {
  const { t } = useTranslation();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const fmtBy = unit === "AMT" ? fmtMoney : fmtQty;
  const planKey = unit === "AMT" ? "planAmt" : "planQty";
  const actualKey = unit === "AMT" ? "actualAmt" : "actualQty";
  const achieveKey = unit === "AMT" ? "achieveAmtPct" : "achieveQtyPct";

  const SERIES = useMemo(() => ({
    plan: ColorUtil.colorSet(COLOR_IDX.plan),
    actual: ColorUtil.colorSet(COLOR_IDX.actual),
    achieve: ColorUtil.colorSet(COLOR_IDX.achieve),
  }), []);

  const legend = useMemo(() => [
    { key: "plan", label: t("spSalesPlan"), color: SERIES.plan },
    { key: "actual", label: t("spSalesActual"), color: SERIES.actual },
    { key: "achieve", label: `${t("spAchieveRate")} (%)`, color: SERIES.achieve },
  ], [t, SERIES]);

  // 시리즈 표시 = 설정 activeFlg. 범례 체크박스와 설정 모달이 같은 소스(chartConfig)를 공유해 동기화된다.
  const isActive = (key) => chartConfig?.[key]?.activeFlg !== false;

  // 제목(=드릴 링크) hover 를 카드 하이라이트로 펼친다 — 자식 hover 로 부모를 못 칠해서 상태로 올린다.
  const [drillHover, setDrillHover] = useState(false);

  return (
    <Card sx={{
      flex: 1.6, minHeight: 0, display: "flex", flexDirection: "column",
      transition: CARD_HOVER_TRANSITION,
      ...(drillHover && CARD_HOVER_SX),
    }}>
      {/* 드릴 클릭 영역은 헤더 제목뿐 — 카드 전체로 잡으면 범례·툴팁 클릭마다 탭이 튄다(§9-1) */}
      <DashboardCardHeader
        title={t("spPlanStatus")} onOpenConfig={onOpenConfig}
        onDrill={onDrill} drillTitle={t("menuDpPlanVsActual")} onDrillHover={setDrillHover}
      />
      <DashboardComposedChart
        data={data} xKey="name"
        yAxes={[
          { id: "left", tickFormatter: fmtBy },
          { id: "right", orientation: "right", tickFormatter: (v) => `${v}%` },
        ]}
        tooltipFormatter={(v, n) => (n === `${t("spAchieveRate")} (%)` ? `${v}%` : fmtBy(v))}
      >
        <defs>{areaGradientDefs([
          { dataKey: planKey, color: SERIES.plan },
          { dataKey: actualKey, color: SERIES.actual },
        ])}</defs>
        {/* 실적/계획 경계 — 실적선이 왜 끊기는지 보는 사람이 짐작하지 않게 한다 */}
        {currentBucket && (
          <ReferenceLine x={currentBucket} yAxisId="left" stroke={SERIES.achieve} strokeDasharray="3 3"
            label={{ value: t("spNow"), position: "top", fontSize: 10, fill: SERIES.achieve }} />
        )}
        {isActive("plan") && barOrLine(chartConfig, { key: "plan", dataKey: planKey, name: t("spSalesPlan"), color: SERIES.plan, yAxisId: "left" })}
        {isActive("actual") && barOrLine(chartConfig, { key: "actual", dataKey: actualKey, name: t("spSalesActual"), color: SERIES.actual, yAxisId: "left", dflt: "AREA" })}
        {isActive("achieve") && barOrLine(chartConfig, { key: "achieve", dataKey: achieveKey, name: `${t("spAchieveRate")} (%)`, color: SERIES.achieve, yAxisId: "right", dflt: "LINE" })}
      </DashboardComposedChart>
      <SeriesLegend items={legend} isActive={isActive} onToggle={onToggleSeries} />
    </Card>
  );
};

// ─────────────────────────────────────────
// 예측 대비 계획 — 통계예측(BF)을 사람이 얼마나 움직였나
//   ★ bf_amt 컬럼이 없다 → AMT 단위에서는 BF 시리즈를 그리지 않는다(백엔드도 안 내린다).
// ─────────────────────────────────────────
const ForecastVsPlanChart = ({ data, currencyCd, unit, chartConfig, onOpenConfig, onToggleSeries, onDrill }) => {
  const { t } = useTranslation();
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const fmtBy = unit === "AMT" ? fmtMoney : fmtQty;
  const isAmt = unit === "AMT";

  const SERIES = useMemo(() => ({
    bf: ColorUtil.colorSet(COLOR_IDX.bf),
    sf: ColorUtil.colorSet(COLOR_IDX.sf),
    mf: ColorUtil.colorSet(COLOR_IDX.mf),
  }), []);

  // BF 는 금액 컬럼이 없다 → AMT 에선 시리즈도 범례도 빼야 한다(체크박스만 남으면 "왜 안 그려지지" 가 된다)
  const legend = useMemo(() => [
    ...(isAmt ? [] : [{ key: "bf", label: t("spBaselineForecast"), color: SERIES.bf }]),
    { key: "sf", label: t("spSalesForecast"), color: SERIES.sf },
    { key: "mf", label: t("managerForecast"), color: SERIES.mf },
  ], [t, isAmt, SERIES]);

  const isActive = (key) => chartConfig?.[key]?.activeFlg !== false;
  const [drillHover, setDrillHover] = useState(false);

  return (
    <Card sx={{
      flex: 1.4, minHeight: 0, display: "flex", flexDirection: "column",
      transition: CARD_HOVER_TRANSITION,
      ...(drillHover && CARD_HOVER_SX),
    }}>
      <DashboardCardHeader
        title={t("spForecastVsPlan")} onOpenConfig={onOpenConfig}
        onDrill={onDrill} drillTitle={t("menuBfAccuracyReport")} onDrillHover={setDrillHover}
      >
        {/* 단가로 환산하면 다른 카드와 금액 소스가 갈리므로, BF 를 숨긴다는 사실을 노출한다 */}
        {isAmt && <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("spBfHiddenInAmt")}</Typography>}
      </DashboardCardHeader>
      <DashboardComposedChart
        data={data} xKey="name"
        yAxes={[{ id: "left", tickFormatter: fmtBy }]}
        tooltipFormatter={(v) => fmtBy(v)}
      >
        <defs>{areaGradientDefs([
          { dataKey: isAmt ? "mfAmt" : "mfQty", color: SERIES.mf },
        ])}</defs>
        {!isAmt && isActive("bf") && barOrLine(chartConfig, { key: "bf", dataKey: "bfQty", name: t("spBaselineForecast"), color: SERIES.bf, yAxisId: "left", dflt: "LINE" })}
        {isActive("sf") && barOrLine(chartConfig, { key: "sf", dataKey: isAmt ? "sfAmt" : "sfQty", name: t("spSalesForecast"), color: SERIES.sf, yAxisId: "left", dflt: "LINE" })}
        {isActive("mf") && barOrLine(chartConfig, { key: "mf", dataKey: isAmt ? "mfAmt" : "mfQty", name: t("managerForecast"), color: SERIES.mf, yAxisId: "left" })}
      </DashboardComposedChart>
      <SeriesLegend items={legend} isActive={isActive} onToggle={onToggleSeries} />
    </Card>
  );
};

// 분포 카드 컬럼 폭 — 헤더 라벨과 값이 이 상수를 공유해야 세로가 맞는다
const DIST_COL = { value: 78, share: 46, achieve: 54 };

// ─────────────────────────────────────────
// 계획 분포 — 축(품목/거래처) 토글 + 그룹별 달성률
//   비중만 보면 "그래서 뭐가 문제냐" 가 안 나온다. 달성률을 붙여 "어디가 계획을 못 따라가나" 로 바꾼다.
// ─────────────────────────────────────────
const DistributionCard = ({ itemRows, custRows, itemTotal, custTotal, itemLevels, custLevels, itemLevel, custLevel,
  onChangeItemLevel, onChangeCustLevel, currencyCd, unit, closedTo }) => {
  const { t } = useTranslation();
  const [axis, setAxis] = useState("item");
  const fmtMoney = useMemo(() => makeFmtMoney(currencyCd), [currencyCd]);
  const fmtBy = unit === "AMT" ? fmtMoney : fmtQty;

  const isItem = axis === "item";
  const rows = isItem ? itemRows : custRows;
  const totals = isItem ? itemTotal : custTotal;
  const levels = isItem ? itemLevels : custLevels;
  const level = isItem ? itemLevel : custLevel;
  const onChangeLevel = isItem ? onChangeItemLevel : onChangeCustLevel;

  const valueOf = (r) => (unit === "AMT" ? r.amt : r.qty) ?? 0;
  const achieveOf = (r) => (unit === "AMT" ? r.achieveAmtPct : r.achieveQtyPct);
  // 분모는 **절단 전 전체 합계**다 — 보이는 Top 10 만으로 나누면 비중이 부푼다.
  //   상위 10개 밖이 있으면 표시된 비중의 합은 100% 미만이 되고, 그게 사실이다.
  const grandTotal = (unit === "AMT" ? totals?.amt : totals?.qty) ?? 0;
  const total = grandTotal > 0 ? grandTotal : rows.reduce((s, r) => s + valueOf(r), 0);

  return (
    <Card sx={{ flex: 1.2, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <DashboardCardHeader title={t("spDistribution")}>
        {/* 달성률 분모가 "확정 구간 계획" 이라는 걸 숨기지 않는다 — 안 적으면 창 전체로 오해한다.
            DashboardCardHeader 는 subTitle prop 이 없다 — 우측 children 으로 넣는다 */}
        {closedTo && (
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>
            {t("spAchieveNote", { to: closedTo })}
          </Typography>
        )}
        {/* SegToggle 은 문자열 옵션을 t(소문자) 로 번역한다 — 기존 "item"/"customer" 키를 그대로 쓴다 */}
        <SegToggle value={axis} options={["item", "customer"]} onChange={setAxis} />
        {levels.length > 0 && (
          <CompactSelect
            value={level ?? ""}
            options={levels.map((l) => ({ value: l.col, label: l.name }))}
            onChange={onChangeLevel}
          />
        )}
      </DashboardCardHeader>
      {/* 컬럼 라벨 — 세 숫자가 각각 뭔지 hover 없이 항상 보이게 한다.
          달성률만 툴팁을 다는 건 이것만 한 줄로 안 끝나기 때문(분모가 확정 구간이라는 단서가 필요). */}
      {rows.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, pb: 0.3, borderBottom: 1, borderColor: "divider" }}>
          <Box sx={{ flex: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexShrink: 0 }}>
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", minWidth: DIST_COL.value, textAlign: "right" }}>
              {t("plan")}
            </Typography>
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", minWidth: DIST_COL.share, textAlign: "right" }}>
              {t("spColShare")}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.2, minWidth: DIST_COL.achieve }}>
              <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("spColAchieve")}</Typography>
              <Tooltip arrow title={t("spAchieveTooltip", { to: closedTo ?? "" })}>
                <InfoOutlinedIcon sx={{ fontSize: 12, color: "text.disabled", cursor: "help" }} />
              </Tooltip>
            </Box>
          </Box>
        </Box>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.6, mt: 0.5, overflowY: "auto" }}>
        {rows.map((r) => {
          const v = valueOf(r);
          const share = total > 0 ? Math.round((v / total) * 1000) / 10 : 0;
          const achieve = achieveOf(r);
          return (
            <Box key={r.grp}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 1 }}>
                <Typography sx={{ fontSize: FS.BODY, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {r.grp}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, flexShrink: 0 }}>
                  <Typography sx={{ fontSize: FS.SUB, color: "text.primary", fontWeight: 600, minWidth: DIST_COL.value, textAlign: "right" }}>
                    {fmtBy(v)}
                  </Typography>
                  <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary", minWidth: DIST_COL.share, textAlign: "right" }}>
                    {share}%
                  </Typography>
                  {/* 달성률: 실적이 없는 그룹(미래 구간뿐)은 "-" — 0% 로 칠하면 "하나도 못 팔았다" 가 된다 */}
                  <Typography sx={{
                    fontSize: FS.CAPTION, fontWeight: 600, minWidth: DIST_COL.achieve, textAlign: "right",
                    color: achieve == null ? "text.disabled" : (achieve >= 100 ? "success.main" : "warning.main"),
                  }}>
                    {achieve == null ? "-" : `${achieve}%`}
                  </Typography>
                </Box>
              </Box>
              <LinearProgress variant="determinate" value={share} sx={{ height: 4, borderRadius: 2 }} />
            </Box>
          );
        })}
        {rows.length === 0 && (
          <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{t("noData")}</Typography>
        )}
      </Box>
    </Card>
  );
};

// ─────────────────────────────────────────
// 메인
// ─────────────────────────────────────────
const SalesPlanDashboard = ({ viewName }) => {
  const { t } = useTranslation();
  const { isPermitted } = useMenuAccess();   // 권한 밖 화면으로 가는 드릴 링크는 만들지 않는다
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [unit, setUnit] = useState(reduxViewState?.unit ?? "AMT");   // 페이지 공통 단위 토글(상단 하나)
  const [itemLevel, setItemLevel] = useState(reduxViewState?.itemLevel ?? "");
  const [custLevel, setCustLevel] = useState(reduxViewState?.custLevel ?? "");
  const [configOpen, setConfigOpen] = useState(false);
  const [configScope, setConfigScope] = useState("planStatus");
  const openConfig = (scope) => { setConfigScope(scope); setConfigOpen(true); };

  // 집계 레벨만 재조회 트리거(단위 토글은 응답에 두 단위가 다 들어 있어 재조회 불필요)
  const { payload } = useDashboardData(salesPlanService.get, {
    itemLevel: itemLevel || undefined,
    custLevel: custLevel || undefined,
  });

  const { chartConfig, saveChartConfig, toggleSeries } =
    useDashboardP13n(viewName, { defaultChartConfig: DEFAULT_CHART_CONFIG });

  const handleSaveConfig = (cfg) => { saveChartConfig(cfg); setConfigOpen(false); };

  // 드릴스루 — 대상 뷰 상태를 지우고 기간만 심는다(나머지는 대상 페이지 기본값 = All / 최신 RELEASED).
  const drillWithPeriod = (reduxKey, makeState) => {
    const period = payload?.period;
    if (!period?.from || !period?.to) return;
    const [fy, fm] = period.from.split("-").map(Number);
    const [ty, tm] = period.to.split("-").map(Number);
    const startDate = new ZDate(fy, fm - 1, 1, 0, 0, 0);
    const endDate = new ZDate(ty, tm, 0, 23, 59, 59);
    reduxDispatch(reduxUtil.removeViewState({ viewName: reduxKey }));
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(reduxKey, makeState(startDate, endDate))));
    reduxDispatch(reduxUtil.updateHistoryState(reduxKey));
  };

  const openPlanVsActual = () =>
    drillWithPeriod("DP_ACTUAL_PLAN", (startDate, endDate) => ({
      startDate, endDate,
      filter: {}, customerFilterList: {}, itemFilterList: {},   // 거래처·품목 계층 = All
    }));

  // 예측 정확도 평가는 이 화면의 몫이 아니다 — BF 정확도 리포트(수요계획 / 결과)로 넘긴다.
  //   대상은 startDate/endDate 를 실제로 읽는다(BfAccuracyReport 실측) → 기간 주입 패턴이 맞다.
  //   ⚠ 이 리포트는 appRoles: ["ROLE_APP_BF"] 다 — BF 앱이 없는 테넌트에선 메뉴 자체가 없다.
  const openBfAccuracy = () =>
    drillWithPeriod("BF_ACCURACY_REPORT", (startDate, endDate) => ({
      startDate, endDate,
      filter: {},   // 거래처·품목 필터 = All (대상 기본값)
    }));

  // 포지션 마스터로 탭 전환만 한다 — 마스터 화면이라 심을 상태가 없고, 지우면 그쪽 필터만 날아간다.
  const openPostn = () => reduxDispatch(reduxUtil.updateHistoryState("INPUT_POSTN"));

  useEffect(() => {
    const reduxState = reduxUtil.createObjectState(viewName, { unit, itemLevel, custLevel });
    reduxDispatch(reduxUtil.updateViewState({ ...reduxViewState, ...reduxState }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit, itemLevel, custLevel]);

  const kpis = useMemo(() => {
    const rows = payload?.planStatus ?? [];
    const amt = unit === "AMT";
    // 완결된 버킷만으로 달성률을 낸다 — 백엔드가 closed 플래그를 준다(전월까지).
    //   미래 버킷은 물론 **진행 중인 당월도 제외**한다: 실적은 오늘까지인데 계획은 월 전체라
    //   달이 끝나기 전엔 달성률이 구조적으로 눌린다.
    const closed = rows.filter((r) => r.closed && (amt ? r.actualAmt : r.actualQty) != null);
    const closedPlan = closed.reduce((s, r) => s + ((amt ? r.planAmt : r.planQty) ?? 0), 0);
    const closedActual = closed.reduce((s, r) => s + ((amt ? r.actualAmt : r.actualQty) ?? 0), 0);
    return {
      achievePct: closedPlan > 0 ? Math.round((closedActual / closedPlan) * 1000) / 10 : null,
      closedCount: closed.length,
    };
  }, [payload, unit]);

  if (!payload) return null;

  const coverage = unit === "AMT" ? payload.kpi?.coverageAmtPct : payload.kpi?.coverageQtyPct;
  const iv = payload.kpi?.intervention ?? {};
  const progress = payload.progress ?? {};
  const dist = payload.distribution ?? {};

  const configMeasures = {
    planStatus: [
      { key: "plan", label: t("spSalesPlan"), hasActive: true, color: ColorUtil.colorSet(COLOR_IDX.plan) },
      { key: "actual", label: t("spSalesActual"), hasActive: true, color: ColorUtil.colorSet(COLOR_IDX.actual) },
      { key: "achieve", label: t("spAchieveRate"), hasActive: true, color: ColorUtil.colorSet(COLOR_IDX.achieve) },
    ],
    forecastVsPlan: [
      { key: "bf", label: t("spBaselineForecast"), hasActive: true, color: ColorUtil.colorSet(COLOR_IDX.bf) },
      { key: "sf", label: t("spSalesForecast"), hasActive: true, color: ColorUtil.colorSet(COLOR_IDX.sf) },
      { key: "mf", label: t("managerForecast"), hasActive: true, color: ColorUtil.colorSet(COLOR_IDX.mf) },
    ],
  }[configScope] ?? [];

  return (
    <DashboardPage>
      <DashboardTopBar period={payload.period} pinViewName={viewName}>
        <SegToggle value={unit} options={["AMT", "QTY"]} onChange={setUnit} />
      </DashboardTopBar>

      {/* Row 1: KPI 3장 */}
      <DashboardRow kpi>
        {/* 목표(FF) 대비 계획(MF) — 창을 최근 12개월로 맞춰 Sales Status 의 Annual Target 과 어긋나지 않게 한다 */}
        <KpiCard
          label={t("spKpiCoverage")}
          value={coverage == null ? "-" : `${coverage}%`}
          /* measure 이름은 기존 annualTarget 키를 끌어다 쓴다 — 내부 코드(FF)는 번역이 안 되고,
             같은 measure 를 Sales Status 가 이미 그 이름으로 부른다(두 화면의 이름이 어긋나지 않게). */
          sub={t("spKpiCoverageSub", {
            target: t("annualTarget"),
            from: payload.kpi?.coverageFrom ?? "",
            to: payload.kpi?.coverageTo ?? "",
          })}
        />
        <KpiCard
          label={t("spKpiAchieve")}
          value={kpis.achievePct == null ? "-" : `${kpis.achievePct}%`}
          sub={t("spKpiAchieveSub", { count: kpis.closedCount, to: payload.kpi?.closedTo ?? "" })}
          onClick={isPermitted("DP_ACTUAL_PLAN") ? openPlanVsActual : undefined}
        />
        {/* 값은 **절대 개입률**(Σ|MF−BF| ÷ ΣBF) — 순증감만 보면 상향·하향이 상쇄돼
            "아무도 손대지 않았다" 와 구분이 안 된다. 순증감·상향 행수는 보조로 sub 에 둔다.
            ★ bf_amt 가 없어 이 지표는 단위 토글과 무관하게 **항상 수량 기준**이다 — sub 에 명시한다. */}
        <KpiCard
          label={t("spKpiIntervention")}
          value={iv.absPct == null ? "-" : `${iv.absPct}%`}
          sub={iv.absPct == null ? t("spKpiInterventionNoBf") : t("spKpiInterventionSub", {
            net: iv.netPct > 0 ? `+${iv.netPct}` : iv.netPct,
            up: iv.upRows ?? 0,
            cmp: iv.cmpRows ?? 0,
          })}
          right={iv.from && (
            <Typography sx={{ fontSize: FS.CAPTION, color: "text.secondary" }}>{`${iv.from} ~ ${iv.to}`}</Typography>
          )}
          onClick={isPermitted("BF_ACCURACY_REPORT") ? openBfAccuracy : undefined}
        />
      </DashboardRow>

      {/* Row 2: 계획 진행 상황 + 계획 상태 */}
      <DashboardRow>
        {/* 결재 기능이 꺼진 테넌트에서는 카드를 아예 그리지 않는다 */}
        {progress.enabled && <PlanProgressCard progress={progress}
          onOpenPostn={isPermitted("INPUT_POSTN") ? openPostn : undefined} />}
        <PlanStatusChart
          data={payload.planStatus ?? []}
          currencyCd={payload.currencyCd}
          unit={unit}
          currentBucket={payload.period?.current}
          chartConfig={chartConfig}
          onOpenConfig={() => openConfig("planStatus")}
          onToggleSeries={toggleSeries}
          onDrill={isPermitted("DP_ACTUAL_PLAN") ? openPlanVsActual : undefined}
        />
      </DashboardRow>

      {/* Row 3: 예측 대비 계획 + 분포(품목/거래처) */}
      <DashboardRow>
        <ForecastVsPlanChart
          data={payload.forecastPlan ?? []}
          currencyCd={payload.currencyCd}
          unit={unit}
          chartConfig={chartConfig}
          onOpenConfig={() => openConfig("forecastVsPlan")}
          onToggleSeries={toggleSeries}
          onDrill={isPermitted("BF_ACCURACY_REPORT") ? openBfAccuracy : undefined}
        />
        <DistributionCard
          itemRows={dist.item ?? []} custRows={dist.customer ?? []}
          itemTotal={dist.itemTotal} custTotal={dist.custTotal}
          itemLevels={dist.itemLevels ?? []} custLevels={dist.custLevels ?? []}
          itemLevel={itemLevel || dist.itemLevel} custLevel={custLevel || dist.custLevel}
          onChangeItemLevel={setItemLevel} onChangeCustLevel={setCustLevel}
          currencyCd={payload.currencyCd} unit={unit} closedTo={dist.closedTo}
        />
      </DashboardRow>

      <ChartConfigModal
        open={configOpen}
        onClose={() => setConfigOpen(false)}
        config={chartConfig}
        onSave={handleSaveConfig}
        measures={configMeasures}
        chartTypes={DW_CHART_TYPES}
      />
    </DashboardPage>
  );
};

export default SalesPlanDashboard;
